import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class EmailEncryptionService {
  private secretKey: string = `${environment.secretKey}`;

  constructor() {}

  private xorEncryptDecrypt(text: string, key: string): string {
    let result = "";
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  }

  private stringToBase64(str: string): string {
    return btoa(str);
  }

  private base64ToString(base64: string): string {
    return atob(base64);
  }

  private addPadding(text: string): string {
    const randomPrefix = Math.random().toString(36).substring(2, 8);
    const randomSuffix = Math.random().toString(36).substring(2, 8);
    return randomPrefix + "|" + text + "|" + randomSuffix;
  }

  private removePadding(paddedText: string): string {
    const parts = paddedText.split("|");
    return parts.length === 3 ? parts[1] : paddedText;
  }

  async encryptEmail(email: string): Promise<string> {
    try {
      // Import key
      const keyBuffer = new TextEncoder().encode(this.secretKey);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        keyBuffer,
        { name: "AES-GCM" } as AesKeyAlgorithm,
        false,
        ["encrypt"]
      );

      // Generate random IV
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt
      const encodedEmail = new TextEncoder().encode(email);
      const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv } as AesGcmParams,
        cryptoKey,
        encodedEmail
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Convert to base64
      return btoa(String.fromCharCode.apply(null, Array.from(combined)));
    } catch (error) {
      console.error("Encryption failed:", error);
      throw new Error("Failed to encrypt email");
    }
  }

  async decryptEmail(encryptedBase64: string): Promise<string> {
    try {
      // Import key
      const keyBuffer = new TextEncoder().encode(this.secretKey);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        keyBuffer,
        { name: "AES-GCM" } as AesKeyAlgorithm,
        false,
        ["decrypt"]
      );

      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedBase64)
          .split("")
          .map((char) => char.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv } as AesGcmParams,
        cryptoKey,
        encryptedData
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error("Decryption failed:", error);
      throw new Error("Failed to decrypt email");
    }
  }

  encryptEmailSimple(email: string): string {
    const paddedEmail = this.addPadding(email);
    const encrypted = this.xorEncryptDecrypt(paddedEmail, this.secretKey);
    return this.stringToBase64(encrypted);
  }

  decryptEmailSimple(encryptedBase64: string): string {
    try {
      const encrypted = this.base64ToString(encryptedBase64);
      const paddedEmail = this.xorEncryptDecrypt(encrypted, this.secretKey);

      const checkEmailFormat = this.isValidEmail(
        this.removePadding(paddedEmail)
      );

      if (checkEmailFormat) {
        return this.removePadding(paddedEmail);
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }
  isValidEmail(email: string): boolean {
    const emailRegex = /^[\w.-]+@[\w.-]+\.\w{2,4}$/;
    return emailRegex.test(email);
  }

  decryptFormValue(encryptedBase64: string) {
    try {
      const encrypted = this.base64ToString(encryptedBase64);
      const paddedEmail = this.xorEncryptDecrypt(encrypted, this.secretKey);

      return this.removePadding(paddedEmail);
    } catch (error) {
      return null;
    }
  }
}
